/*
 * DER BROWSER-SPEICHER IST ENDLICH — UND BIS 30.09.2026 HAT IHN NIEMAND AUFGERAEUMT.
 *
 * Rafael, 30.09.2026: Absenden an Buehnen tat sichtbar nichts; oben im Guide-Kopf stand
 * „Setting the value of 'guide-send-v1:rafael:…' exceeded the quota“. Gemessen (Bühne
 * b-absenden-tut-nichts-browser-speicher-vol-20260930): jeder Beraterchat einer Buehne
 * (`GuideBeraterChat` -> `CuiLitePanel`) legte `cui-msgs-<sitzung>` mit den letzten 50
 * Nachrichten ab — 37–107 KB je Chat, nie geloescht; Rafael hat vom 21. bis 30.09. 256
 * verschiedene Buehnen gesehen. Chromium gibt einem Ursprung rund 5 Mio Zeichen. Die
 * Sendewarteschlange war nur die erste Stelle, die daran scheiterte — und die einzige,
 * an der ein Scheitern Rafaels Wort kostet.
 *
 * Deshalb zwei Klassen von Schluesseln:
 *  - WEGWERF-CACHES: jederzeit vom Server neu ladbar. Sie werden begrenzt und bei vollem
 *    Speicher als Erstes geopfert.
 *  - alles andere (Sendeauftraege, Entwuerfe, Layouts, Einstellungen): wird hier nie
 *    angefasst. Reicht das Opfern der Caches nicht, wirft `schreibeMitPlatz` laut.
 */
export type Speicher = { readonly length: number; key(index: number): string | null; getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void };

/** Vom Server jederzeit neu ladbar — ein Verlust kostet nur einen Ladevorgang. */
export const WEGWERF_PRAEFIXE = ['cui-msgs-', 'cui-template-', 'cui-convs-'] as const;

export function istSpeicherVoll(e: unknown): boolean {
  if (typeof DOMException !== 'undefined' && e instanceof DOMException) {
    if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || e.code === 22 || e.code === 1014) return true;
  }
  return e instanceof Error && /quota/i.test(e.message);
}

export type BilanzZeile = { familie: string; schluessel: number; zeichen: number };

/** Familie = Schluessel ohne die Kennung am Ende (`cui-msgs-<uuid>` -> `cui-msgs-`). */
export function familieVon(key: string): string {
  const m = key.match(/^(.*?[:\-])(?:n-[a-z0-9-]+|[0-9a-f]{8}-[0-9a-f-]{27,}|[a-z0-9]+-\d{8}.*|b-.*|[A-Za-z0-9_.@%-]{16,})$/);
  return m ? m[1] : key;
}

/** Wer belegt wie viel? Zeichen = Laenge von Schluessel + Wert (Chromium zaehlt so). */
export function speicherBilanz(store: Speicher): BilanzZeile[] {
  const fam = new Map<string, BilanzZeile>();
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i); if (k === null) continue;
    const f = familieVon(k); const z = fam.get(f) ?? { familie: f, schluessel: 0, zeichen: 0 };
    z.schluessel++; z.zeichen += k.length + (store.getItem(k)?.length ?? 0); fam.set(f, z);
  }
  return [...fam.values()].sort((a, b) => b.zeichen - a.zeichen);
}

export const bilanzText = (b: BilanzZeile[], n = 4) =>
  b.slice(0, n).map(z => `${z.familie} ${z.schluessel}× ${Math.round(z.zeichen / 1000)}k`).join(' · ');

/** Entfernt alle Wegwerf-Caches (ausser `behalte`). Nie Sendeauftraege, nie Entwuerfe. */
export function raeumeCachesAuf(store: Speicher, behalte: ReadonlySet<string> = new Set()): { entfernt: number; zeichen: number } {
  const weg: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && !behalte.has(k) && WEGWERF_PRAEFIXE.some(p => k.startsWith(p))) weg.push(k);
  }
  let zeichen = 0;
  for (const k of weg) { zeichen += k.length + (store.getItem(k)?.length ?? 0); store.removeItem(k); }
  return { entfernt: weg.length, zeichen };
}

export class SpeicherVollFehler extends Error {
  readonly speicherVoll = true;
  constructor(readonly schluessel: string, readonly bilanz: BilanzZeile[], ursache?: unknown) {
    super(`Der Browser-Speicher ist voll — auch nach dem Aufräumen der Zwischenspeicher. Größte Belegung: ${bilanzText(bilanz)}. (Browser: ${ursache instanceof Error ? ursache.message : String(ursache ?? '')})`);
    this.name = 'SpeicherVollFehler';
  }
}

/**
 * setItem, das an vollem Speicher nicht stirbt: erst die Wegwerf-Caches opfern, dann noch
 * einmal. Reicht das nicht, fliegt `SpeicherVollFehler` mit der Bilanz — laut, nie still.
 */
export function schreibeMitPlatz(store: Speicher, key: string, value: string): void {
  try { store.setItem(key, value); return; } catch (e) { if (!istSpeicherVoll(e)) throw e; }
  raeumeCachesAuf(store);
  try { store.setItem(key, value); } catch (e) {
    if (!istSpeicherVoll(e)) throw e;
    throw new SpeicherVollFehler(key, speicherBilanz(store), e);
  }
}

/*
 * NACHRICHTEN-CACHE DER CHATS: HOECHSTENS `MAX_NACHRICHTEN_CACHES` SITZUNGEN.
 * Der Cache dient nur dem sofortigen Bild beim Wiederoeffnen eines Chats. Die Liste der
 * zuletzt benutzten Sitzungen steht unter `cui-msgs-reihenfolge` (bewusst ohne den Praefix
 * `cui-msgs-` davor — sie ist kein Cache-Eintrag). Aeltere und nicht gelistete Eintraege
 * (Altbestand vor dem 30.09.2026) fallen beim naechsten Schreiben weg.
 */
export const MAX_NACHRICHTEN_CACHES = 12;
const REIHENFOLGE = 'cui-msgs:reihenfolge';

export function merkeNachrichtenCache(store: Speicher, sessionId: string, wert: string): void {
  let reihe: string[] = [];
  try { const r = JSON.parse(store.getItem(REIHENFOLGE) ?? '[]'); if (Array.isArray(r)) reihe = r.filter(x => typeof x === 'string'); } catch { /* kaputte Liste = leere Liste; es sind nur Caches */ }
  reihe = [sessionId, ...reihe.filter(s => s !== sessionId)].slice(0, MAX_NACHRICHTEN_CACHES);
  const behalten = new Set(reihe.map(s => 'cui-msgs-' + s));
  const weg: string[] = [];
  for (let i = 0; i < store.length; i++) { const k = store.key(i); if (k?.startsWith('cui-msgs-') && !behalten.has(k)) weg.push(k); }
  for (const k of weg) store.removeItem(k);
  // Ein Cache ist es nicht wert, fremde Caches zu opfern oder laut zu scheitern: passt er
  // nicht, gibt es ihn eben nicht (der Chat laedt dann vom Server).
  try { store.setItem('cui-msgs-' + sessionId, wert); store.setItem(REIHENFOLGE, JSON.stringify(reihe)); }
  catch (e) { if (!istSpeicherVoll(e)) throw e; store.removeItem('cui-msgs-' + sessionId); }
}

/*
 * SOFORT-AUFRAEUMUNG, EINMAL JE BROWSER (Rafael ueber den Kanzler, 30.09.2026: *„Räum mir
 * alles auf, was wir nicht mehr brauchen"*). Beim Laden der Oberflaeche, vor allem anderen:
 *  - entfernt `cui-msgs-*` und `cui-convs-*` — reine Caches, der Server hat den Inhalt;
 *  - schrumpft jeden `guide-send-v1:*`-Eintrag auf das Noetige (`verkleinere`), statt ihn zu
 *    loeschen: kein Sendeauftrag geht verloren, gleich in welchem Zustand;
 *  - fasst Layouts (`cui-layout-*`, `cui-template-*`), Arbeitsflaechen und Entwuerfe NICHT an.
 * Ergebnis laut ins Console-Log (Anzahl, KB vorher/nachher) — `main.tsx`.
 */
export const SOFORT_MARKE = 'cui-speicher-aufgeraeumt:v1';
export const SOFORT_CACHES = ['cui-msgs-', 'cui-convs-'] as const;
export type SofortBericht = { entfernt: number; verkleinert: number; unlesbar: number; kbVorher: number; kbNachher: number };

const gesamtZeichen = (store: Speicher) => { let n = 0; for (let i = 0; i < store.length; i++) { const k = store.key(i); if (k !== null) n += k.length + (store.getItem(k)?.length ?? 0); } return n; };
const kb = (zeichen: number) => Math.round(zeichen * 2 / 1024); // UTF-16: zwei Byte je Zeichen

export function sofortAufraeumen(store: Speicher, verkleinere: (wert: string) => string): SofortBericht | null {
  if (store.getItem(SOFORT_MARKE) !== null) return null;
  const vorher = gesamtZeichen(store);
  const alle: string[] = []; for (let i = 0; i < store.length; i++) { const k = store.key(i); if (k !== null) alle.push(k); }
  let entfernt = 0, verkleinert = 0, unlesbar = 0;
  for (const k of alle) {
    if (SOFORT_CACHES.some(p => k.startsWith(p))) { store.removeItem(k); entfernt++; continue; }
    if (!k.startsWith('guide-send-v1:')) continue;
    const alt = store.getItem(k); if (alt === null) continue;
    let neu: string;
    try { neu = verkleinere(alt); } catch { unlesbar++; continue; } // unverstanden = unangetastet
    // Ein kuerzerer Wert passt immer an die Stelle des laengeren — dieses setItem kann nicht am Kontingent scheitern.
    if (neu.length < alt.length) { store.setItem(k, neu); verkleinert++; }
  }
  const bericht = { entfernt, verkleinert, unlesbar, kbVorher: kb(vorher), kbNachher: kb(gesamtZeichen(store)) };
  store.setItem(SOFORT_MARKE, JSON.stringify({ am: new Date().toISOString(), ...bericht }));
  return bericht;
}
